import { Component } from '@theme/component';

/**
 * `<comparison-cta>` — buy button below the comparison table. Forwards the
 * click to the PDP's main add-to-cart button, but runs the fly-to-cart
 * animation from THIS button: the buybox is scrolled far off-screen here,
 * so the theme's own animation (sourced at the buybox button) would fly in
 * invisibly from above the viewport.
 *
 * @extends {Component}
 */
class ComparisonCta extends Component {
  /**
   * @param {Event} event
   */
  triggerAddToCart(event) {
    const addToCartButton = document.querySelector('.button.add-to-cart-button');
    if (!(addToCartButton instanceof HTMLElement)) return;

    event.preventDefault();

    // Suppress the buybox's own (off-screen) fly animation for this click.
    const addToCartComponent = addToCartButton.closest('add-to-cart-component');
    const previous = addToCartComponent instanceof HTMLElement ? addToCartComponent.dataset.addToCartAnimation : undefined;
    if (addToCartComponent instanceof HTMLElement) addToCartComponent.dataset.addToCartAnimation = 'false';

    addToCartButton.click();

    if (addToCartComponent instanceof HTMLElement && previous !== undefined) {
      addToCartComponent.dataset.addToCartAnimation = previous;
    }

    if (previous === 'true' && event.target instanceof HTMLElement) {
      this.#animateFlyToCart(event.target, addToCartComponent);
    }
  }

  /**
   * Spawns the theme's `fly-to-cart` element sourced at our own button.
   *
   * @param {HTMLElement} sourceButton
   * @param {Element | null} addToCartComponent
   */
  #animateFlyToCart(sourceButton, addToCartComponent) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!customElements.get('fly-to-cart')) return;

    const image = addToCartComponent instanceof HTMLElement ? addToCartComponent.dataset.productVariantMedia : null;
    const cartIcon = this.#getVisibleCartIcon();
    if (!image || !cartIcon) return;

    const flyToCartElement = /** @type {HTMLElement & { source: Element, destination: Element, arc?: object }} */ (
      document.createElement('fly-to-cart')
    );
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = sourceButton;
    flyToCartElement.destination = cartIcon;

    // Same arc flattening as product-form.js: the floating cart is pinned
    // near the top, keep the upward path from overshooting the viewport.
    if (cartIcon.closest('.floating-cart')) {
      flyToCartElement.arc = { liftStart: 80, liftEnd: 0, pullEnd: 120 };
    }

    document.body.appendChild(flyToCartElement);
  }

  /**
   * The cart icon currently on screen: the floating cart when visible,
   * otherwise the header cart (mirrors product-form.js).
   *
   * @returns {Element | null}
   */
  #getVisibleCartIcon() {
    const floatingCart = document.querySelector('.floating-cart');
    if (floatingCart instanceof HTMLElement) {
      const style = getComputedStyle(floatingCart);
      const rect = floatingCart.getBoundingClientRect();
      const visible =
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        parseFloat(style.opacity) > 0 &&
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight;
      if (visible) return floatingCart.querySelector('.header-actions__cart-icon') ?? floatingCart;
    }
    return (
      document.querySelector('#header-component .header-actions__cart-icon') ??
      document.querySelector('.header-actions__cart-icon')
    );
  }
}

if (!customElements.get('comparison-cta')) {
  customElements.define('comparison-cta', ComparisonCta);
}
